// Dialogs can open over each other: a confirm over Settings, a match over a name page. Only the
// top one answers Escape and keeps Tab inside itself.

const stack: HTMLElement[] = [];

/** Whether `el` is the top dialog. One that has already left the page doesn't count, even before
 * its cleanup has taken it off the stack. */
export function isTopModal(el: HTMLElement): boolean {
  for (let i = stack.length - 1; i >= 0; i--) if (stack[i].isConnected) return stack[i] === el;
  return false;
}

/** Puts a dialog on top; call what it returns when the dialog closes. */
export function pushModal(el: HTMLElement): () => void {
  stack.push(el);
  return () => {
    const i = stack.indexOf(el);
    if (i >= 0) stack.splice(i, 1);
  };
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]';

/** Tab and Shift-Tab go round the controls in `box` instead of leaving it. */
export function trapTab(ev: KeyboardEvent, box: HTMLElement): void {
  if (ev.key !== "Tab") return;
  const items = [...box.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.tabIndex >= 0 && el.getClientRects().length > 0);
  if (!items.length) return ev.preventDefault();
  const at = document.activeElement;
  const inside = !!at && box.contains(at);
  // whether b comes after a in the page
  const after = (a: Node, b: Node) => !!(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING);
  if (ev.shiftKey) {
    if (!inside || !items.some((el) => el !== at && after(el, at!))) {
      ev.preventDefault();
      items[items.length - 1].focus();
    }
  } else if (!inside || !items.some((el) => el !== at && after(at!, el))) {
    ev.preventDefault();
    items[0].focus();
  }
}

/** Focus goes back where it was, if that's still on the page. */
export function restoreFocus(el: Element | null): void {
  if (el instanceof HTMLElement && el.isConnected && el !== document.body) el.focus({ preventScroll: true });
}
